import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, AdminLayout } from "./AdminLayout";
import { FileText, Newspaper, Image, MessageSquare, TrendingUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState({
    pages: 0,
    blogs: 0,
    galleries: 0,
    contactSubmissions: 0,
    processSubmissions: 0,
  });
  const [recentSubmissions, setRecentSubmissions] = useState([]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [pages, blogs, galleries, contact, process] = await Promise.all([
          axios.get(`${API}/pages`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/blogs`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/galleries`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/contact/submissions`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/process/submissions`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);

        setStats({
          pages: pages.data.length,
          blogs: blogs.data.length,
          galleries: galleries.data.length,
          contactSubmissions: contact.data.length,
          processSubmissions: process.data.length,
        });

        // Get 5 most recent submissions (combined)
        const allSubmissions = [
          ...contact.data.map((s) => ({ ...s, type: "contact" })),
          ...process.data.map((s) => ({ ...s, type: "process" })),
        ]
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 5);

        setRecentSubmissions(allSubmissions);
      } catch (error) {
        console.error("Error fetching stats:", error);
      }
    };

    fetchStats();
  }, [token]);

  const statCards = [
    { label: "Pages", value: stats.pages, icon: FileText, color: "text-blue-400" },
    { label: "Blog Posts", value: stats.blogs, icon: Newspaper, color: "text-green-400" },
    { label: "Galleries", value: stats.galleries, icon: Image, color: "text-purple-400" },
    { label: "Contact Msgs", value: stats.contactSubmissions, icon: MessageSquare, color: "text-yellow-400" },
    { label: "Project Inquiries", value: stats.processSubmissions, icon: TrendingUp, color: "text-its-gold" },
  ];

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (err) {
      console.debug("Date parse failed:", err);
      return dateString;
    }
  };

  const getSubmissionPreview = (sub) => {
    if (sub.type === "contact") return sub.message || "";
    // Process submissions store extra fields as JSON in description
    try {
      const parsed = JSON.parse(sub.description || "{}");
      const parts = [parsed.products_services, parsed.business_goals, parsed.website_purpose, parsed.additional_info]
        .filter(Boolean);
      if (parts.length > 0) return parts[0];
    } catch (err) { console.debug("Description not JSON:", err); }
    return sub.company || sub.project_type || sub.description || "No details provided";
  };

  const getSubmissionName = (sub) => {
    if (sub.name) return sub.name;
    if (sub.type === "process") return sub.company || sub.email || "Anonymous";
    return "Anonymous";
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-dashboard">
        <h1 className="heading-3">Dashboard</h1>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {statCards.map((stat) => (
            <div key={stat.label} className="glass-card">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg bg-its-teal-secondary/30 ${stat.color}`}>
                  <stat.icon size={20} />
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">{stat.value}</div>
                  <div className="text-xs text-its-offwhite/60">{stat.label}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Recent Submissions */}
        <div className="glass-card">
          <h2 className="text-lg font-semibold text-white mb-4">Recent Submissions</h2>
          {recentSubmissions.length === 0 ? (
            <p className="text-its-offwhite/60 text-center py-4">No submissions yet</p>
          ) : (
            <div className="space-y-3">
              {recentSubmissions.map((sub) => (
                <div
                  key={sub.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-its-teal/50 border border-its-teal-light/10"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-white">{getSubmissionName(sub)}</span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded ${
                          sub.type === "contact"
                            ? "bg-blue-500/20 text-blue-400"
                            : "bg-its-gold/20 text-its-gold"
                        }`}
                      >
                        {sub.type === "contact" ? "Contact" : "Project"}
                      </span>
                    </div>
                    <p className="text-sm text-its-offwhite/60 line-clamp-1">
                      {getSubmissionPreview(sub) || "No details provided"}
                    </p>
                  </div>
                  <div className="text-xs text-its-offwhite/40">{formatDate(sub.created_at)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
